/**
 * AssessmentsPanel styles — token-driven to match the calendar's editorial
 * look (Calendar.css): theme surfaces instead of hardcoded whites, dashed
 * strong borders on callouts, mono uppercase micro-labels.
 */
import React from "react";

// Spinner animation style
export const spin: React.CSSProperties = {
  animation: "ap-spin 1s linear infinite",
};

// Main styles object
export const styles = {
  sectionTitle: { fontWeight: 800, fontSize: 14, color: "var(--color-title)" } as React.CSSProperties,

  tile: {
    border: "1px solid var(--color-border)",
    borderRadius: 12,
    background: "var(--color-surface)",
    padding: 12,
    display: "grid",
    gap: 8,
    cursor: "pointer",
    boxShadow: "var(--shadow-sm)",
  } as React.CSSProperties,

  tileHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  } as React.CSSProperties,

  tileTitle: {
    fontWeight: 800,
    color: "var(--color-title)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as React.CSSProperties,

  tileMeta: { fontSize: 12, color: "var(--color-muted)" } as React.CSSProperties,

  tileActions: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 2,
  } as React.CSSProperties,

  backdrop: {
    position: "fixed",
    inset: 0,
    background: "color-mix(in srgb, var(--color-title) 35%, transparent)",
    zIndex: 9998,
  } as React.CSSProperties,

  drawer: {
    position: "fixed",
    right: 0,
    top: 0,
    height: "100vh",
    width: "min(740px, 92vw)",
    background: "var(--color-surface)",
    color: "var(--color-ink)",
    zIndex: 9999,
    borderLeft: "1px solid var(--color-border)",
    boxShadow: "var(--shadow-lg)",
    padding: 14,
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    gap: 12,
  } as React.CSSProperties,

  drawerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  } as React.CSSProperties,

  // Editorial micro-label, matching the calendar's gutter/invoice labels.
  label: {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "var(--color-muted)",
  } as React.CSSProperties,

  input: {
    width: "100%",
    borderRadius: 10,
    border: "1px solid var(--color-border-strong)",
    background: "var(--color-surface)",
    color: "var(--color-ink)",
    padding: "10px 10px",
    fontSize: 13,
    outline: "none",
  } as React.CSSProperties,

  textarea: {
    width: "100%",
    borderRadius: 10,
    border: "1px solid var(--color-border-strong)",
    background: "var(--color-surface)",
    color: "var(--color-ink)",
    padding: "10px 10px",
    fontSize: 13,
    outline: "none",
    lineHeight: 1.5,
    fontFamily: "var(--font-mono)",
    resize: "vertical",
    minHeight: 180,
  } as React.CSSProperties,

  resultBox: {
    borderRadius: 12,
    border: "1px dashed var(--color-border-strong)",
    background: "var(--color-subsurface)",
    color: "var(--color-ink)",
    padding: 12,
    overflow: "auto",
    maxHeight: 420,
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: "var(--font-mono)",
  } as React.CSSProperties,

  row: { display: "grid", gap: 6 } as React.CSSProperties,

  card: {
    border: "1px solid var(--color-border)",
    borderRadius: 12,
    background: "var(--color-surface)",
    padding: 14,
    display: "grid",
    gap: 10,
  } as React.CSSProperties,

  cardHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
  } as React.CSSProperties,

  grid: { display: "grid", gap: 10 } as React.CSSProperties,
};

// Button style factories
export const btnIconGhost = (color = "var(--color-ink)"): React.CSSProperties => ({
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  borderRadius: 10,
  padding: "6px 8px",
  cursor: "pointer",
  color,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

export const btnFilled = (bg = "var(--color-brand)", fg = "#fff"): React.CSSProperties => ({
  border: "1px solid transparent",
  background: bg,
  color: fg,
  borderRadius: 10,
  padding: "8px 10px",
  cursor: "pointer",
  fontWeight: 800,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
});

export const btnPlain: React.CSSProperties = {
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  color: "var(--color-ink)",
  borderRadius: 10,
  padding: "8px 10px",
  cursor: "pointer",
  fontWeight: 800,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
};

// Status helpers
export type ParseStatus = "pending" | "ok" | "error";

export function statusTone(s?: ParseStatus): "ok" | "warn" | "bad" | "muted" {
  if (s === "ok") return "ok";
  if (s === "pending") return "warn";
  if (s === "error") return "bad";
  return "muted";
}

export function statusLabel(s?: ParseStatus): string {
  if (s === "ok") return "Parsed";
  if (s === "pending") return "Parsing";
  if (s === "error") return "Error";
  return "Not parsed";
}
